import { useEffect, useRef } from "react";
import { trackPlg } from "plg/analytics/events";
import type { FilterType } from "plg/analytics/events";

const TYPES: FilterType[] = ["role", "tools", "language"];

type Filters = Record<FilterType, string[]>;

const sig = (values: string[]) => values.join("\u0000");

/**
 * filter_applied (one per filter type that changed to a non-empty value) once the list has loaded, so it
 * carries results_count; search_returned_empty when those filters found nobody.
 * `total` is null while the list failed to load. While `paused` (the customer's own apps being preselected), a
 * change only moves the baseline: it is not the customer's pick.
 */
export default function usePlgFilterTracking(
  enabled: boolean,
  filters: Filters,
  isLoading: boolean,
  total: number | null,
  paused = false
): void {
  const prev = useRef(filters);
  const pending = useRef<Set<FilterType>>(new Set());

  useEffect(() => {
    if (!paused) {
      TYPES.forEach((t) => {
        if (sig(prev.current[t]) !== sig(filters[t]) && filters[t].length) pending.current.add(t);
      });
    }
    prev.current = filters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig(filters.role), sig(filters.tools), sig(filters.language), paused]);

  useEffect(() => {
    if (isLoading || !pending.current.size) return;
    const changed = [...pending.current];
    pending.current.clear();
    if (!enabled || total === null) return;
    changed.forEach((filter_type) => trackPlg("filter_applied", { filter_type, value: filters[filter_type], results_count: total }));
    if (total === 0) trackPlg("search_returned_empty", { filters_active: TYPES.filter((t) => filters[t].length) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, total]);
}
